import { memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BookOutlined } from '@ant-design/icons';
import { Button, Flex, Skeleton } from 'antd';

import { useAntMessage } from '@/shared/lib/hooks';
import { RichTextEditor } from '@/shared/ui/rich-text-editor';

import {
    useLazyGetAccountCalendarNoteQuery,
    useSaveAccountCalendarNoteMutation,
} from '../../api/trading-platform.api';
import { useGetTradingPlatformSessionId } from '../../models/selectors/get-trading-platform-session-id';
import { useGetTradingPlatformSessionType } from '../../models/selectors/get-trading-platform-session-type';

import cls from './trading-platform-daily-info-notes.module.css';

interface TradingPlatformDailyInfoNotesProps {
    date: DateString;
}

const isRichTextEmpty = (content: string | undefined): boolean => {
    if (!content) return true;

    const textOnly = content.replace(/<[^>]*>/g, '');

    return textOnly.trim().length === 0;
};

export const TradingPlatformDailyInfoNotes = memo(
    (props: TradingPlatformDailyInfoNotesProps) => {
        const { date } = props;

        const { t } = useTranslation('trading-journal');

        const accountId = useGetTradingPlatformSessionId();
        const connectionType = useGetTradingPlatformSessionType();

        const message = useAntMessage();

        const [getAccountCalendarNoteQuery, { data, isFetching }] =
            useLazyGetAccountCalendarNoteQuery();
        const [saveAccountCalendarNoteMutation, { isLoading, isSuccess }] =
            useSaveAccountCalendarNoteMutation();

        const [isEditMode, setIsEditMode] = useState<boolean>(false);
        const [value, setValue] = useState<string>();

        const onSave = useCallback(() => {
            if (accountId && connectionType) {
                if (value && new Blob([value]).size / 1024 / 1024 > 4) {
                    message.error?.({
                        content: t('tradingJournalNoteSizeBig'),
                    });
                } else {
                    saveAccountCalendarNoteMutation({
                        id: accountId,
                        connectionType,
                        date,
                        note: value ?? '',
                    });
                }
            }
        }, [
            accountId,
            connectionType,
            date,
            message,
            saveAccountCalendarNoteMutation,
            t,
            value,
        ]);

        const onCancel = useCallback(() => {
            setIsEditMode(false);
        }, []);

        const addNote = useCallback(() => {
            setValue(data?.note ?? '');
            setIsEditMode(true);
        }, [data?.note]);

        useEffect(() => {
            if (accountId && connectionType) {
                getAccountCalendarNoteQuery({
                    id: accountId,
                    connectionType,
                    date,
                });
            }
        }, [accountId, date, connectionType, getAccountCalendarNoteQuery]);

        useEffect(() => {
            if (isSuccess && accountId && connectionType) {
                setIsEditMode(false);
                getAccountCalendarNoteQuery({
                    id: accountId,
                    connectionType,
                    date,
                });
            }
        }, [
            isSuccess,
            accountId,
            connectionType,
            date,
            getAccountCalendarNoteQuery,
        ]);

        if (!isEditMode) {
            if (isFetching) {
                return (
                    <Flex justify="end">
                        <Skeleton.Button
                            active
                            size="large"
                        />
                    </Flex>
                );
            }

            if (data && !isRichTextEmpty(data.note)) {
                return (
                    <Flex
                        className={cls.preview}
                        vertical
                    >
                        <RichTextEditor
                            key="preview"
                            className={cls.previewContent}
                            isReadOnly
                            value={data.note}
                        />
                        <Flex
                            className={cls.previewControls}
                            justify="end"
                        >
                            <Button
                                icon={<BookOutlined />}
                                onClick={addNote}
                                size="large"
                                type="primary"
                            >
                                {t('tradingJournalNotesButtonNote')}
                            </Button>
                        </Flex>
                    </Flex>
                );
            }

            return (
                <Flex justify="end">
                    <Button
                        icon={<BookOutlined />}
                        onClick={addNote}
                        size="large"
                        type="primary"
                    >
                        {t('tradingJournalNotesButtonAddNote')}
                    </Button>
                </Flex>
            );
        }

        return (
            <Flex
                className={cls.root}
                vertical
            >
                <RichTextEditor
                    key="editor"
                    className={cls.editor}
                    onChange={setValue}
                    placeholder={t('tradingJournalNotesPlaceholder')}
                    value={value ?? ''}
                />

                <Flex
                    className={cls.controls}
                    gap={8}
                    justify="end"
                >
                    <Button
                        loading={isLoading}
                        onClick={onSave}
                        size="large"
                        type="primary"
                    >
                        {t('tradingJournalNotesSaveButton')}
                    </Button>
                    <Button
                        loading={isLoading}
                        onClick={onCancel}
                        size="large"
                    >
                        {t('tradingJournalNotesCancelButton')}
                    </Button>
                </Flex>
            </Flex>
        );
    },
);

TradingPlatformDailyInfoNotes.displayName = 'TradingPlatformDailyInfoNotes';
